/**
 * THE DOOR.
 *
 * One form that does both jobs. Asking somebody to find a different link
 * to make an account is a step people fail at, and the two forms are the
 * same two fields.
 *
 * The third state is the one the old system had no screen for: signed in,
 * but attached to no shop. Row level security means that person sees empty
 * tables everywhere, which looks exactly like a broken query. It gets its
 * own words here.
 */

import {
  signIn, signUp, signOut, sendReset, joinTestShop, signInWithGoogle, googleSignInAvailable, type Who,
} from "../data/session.ts";
import { configNow, api } from "../data/client.ts";
import { esc, mount } from "../ui/dom.ts";
import { mainSiteOf } from "../domain/mainsite.ts";

type Mode = "in" | "up" | "optin";

/**
 * ─── RV DESK'S DOOR ────────────────────────────────────────────────────
 *
 * Anthony, 2026-09-26: "If you hit the back end and can't login, you
 * should get an email only opt in funnel."
 *
 * On an rvdesk.com host there is no "Create a login" tab. A login makes an
 * empty account that belongs to no shop (renderNoProfile below), which is
 * a dead end for somebody who only wanted to know what RV Desk is. They
 * get one box instead: their email, kept by server/routes/rvdesk-optin.ts.
 * And a sign-in that fails turns into that box, with the address they
 * typed already in it, because "wrong password" is the wrong answer to
 * somebody who never had one.
 */
const isRvDesk = (): boolean => configNow().platform === "rvdesk" && !configNow().shop;

/**
 * ─── WHOSE DOOR THIS IS ────────────────────────────────────────────────
 *
 * Anthony, 2026-09-16: "Landing page needs to be a login screen for
 * customer when we do customer shop desk with their branding... not the
 * supercanic one."
 *
 * On a shop's own domain the root now lands here, so this screen is the
 * first thing that shop sees. It says their name over their initials
 * rather than "Shop Desk", and carries their phone number underneath --
 * because the other person who reaches a shop's front door by typing it is
 * one of their customers, and a login they cannot use should not be a dead
 * end. `shop` is null on Supercanic's hosts, where this reads exactly as
 * it always did.
 */
const brandName = (): string => {
  const s = configNow().shop;
  if (!s) return isRvDesk() ? "RV Shop Desk" : "Shop Desk";
  return s.name.replace(/,?\s*(llc|inc|ltd|co)\.?$/i, "").trim() || s.name;
};

/**
 * THE NAME, TYPESET, RATHER THAN THE NAME IN THE UI FONT.
 *
 * Anthony, 2026-09-17: "i want the typographic wordmark on the login
 * screen then." NYIA's own site has no logo FILE at all -- their header is
 * text: "New York <em>Intelligence</em> Agency", the middle word italic in
 * their accent. There was nothing to download, so this reproduces the
 * treatment instead of asking them for an image they do not have.
 *
 * Generic on purpose. Any shop whose name is three or more words gets its
 * middle word emphasized; two words or one are left plain, because
 * italicising half of "Swift Garage" is a mark nobody chose. A shop with a
 * real logo still shows it -- this is the line underneath, not a
 * replacement for a file somebody paid for.
 */
function wordmark(name: string): string {
  const words = name.split(/\s+/).filter(Boolean);
  if (words.length < 3) return esc(name);
  const mid = Math.floor(words.length / 2);
  return words
    .map((w, i) => (i === mid ? `<em>${esc(w)}</em>` : esc(w)))
    .join(" ");
}

/** Their logo if they have one, otherwise their initials on a square --
 *  the same mark the tab favicon uses. */
function brandMark(): string {
  const s = configNow().shop;
  if (!s) return "";
  /* A real logo beats initials every time. It is their own file on their
     own site, so it is the one they consider current -- see 0116. */
  if (s.logoUrl) {
    /* Both, and CSS picks -- the theme can change under this screen. */
    const light = s.logoUrlLight ?? s.logoUrl;
    return `<img class="gate-logo on-dark" src="${esc(s.logoUrl)}" alt="${esc(brandName())}"
      onerror="this.remove()">`
      + `<img class="gate-logo on-light" src="${esc(light)}" alt="${esc(brandName())}"
      onerror="this.remove()">`;
  }
  /* Words with no letter in them are dropped first. "24/7 Swift Garage
     Door" initialled naively reads "2S", which is not a mark anybody
     recognizes; skipping the 24/7 gives SG. */
  const initials = brandName().split(/\s+/)
    .filter((w) => /[A-Za-z]/.test(w))
    .map((w) => w.replace(/[^A-Za-z]/g, "")[0] ?? "").join("").slice(0, 2).toUpperCase();
  if (!initials) return "";
  return `<div class="gate-mark" aria-hidden="true">${esc(initials)}</div>`;
}

/** Their number and their main site, under the form. Nothing when we are
 *  not on a shop's own domain. */
function brandFoot(): string {
  const s = configNow().shop;
  /* RV Desk's door used to carry the cooperative line here. Anthony,
     2026-09-28, asked for it off the login page. */
  if (!s) return "";
  const d10 = String(s.phone ?? "").replace(/\D/g, "").slice(-10);
  const pretty = d10.length === 10
    ? `(${d10.slice(0, 3)}) ${d10.slice(3, 6)}-${d10.slice(6)}` : String(s.phone ?? "").trim();
  /* wervshop.clickflame.com links to werv.clickflame.com, not to itself. */
  const site = mainSiteOf(s.domain);
  return `<div class="gate-brandfoot">
      ${d10.length === 10 || pretty
        ? `<a class="gate-call" href="tel:${esc(String(s.phone ?? "").replace(/[^\d+]/g, ""))}">Call ${esc(pretty)}</a>` : ""}
      <a class="gate-site" href="https://${esc(site)}">${esc(site)}</a>
    </div>`;
}

/**
 * THE WAY TO BUY IT, NOT ONLY TO HEAR ABOUT IT.
 *
 * Anthony, 2026-09-27: "make sure you add our authorize.net so people can
 * sign up." The opt-in box keeps an email; /start takes a card and makes
 * the account (server/routes/rvdesk-start.ts). Both sit on this screen,
 * because the person who came to the back door ready to pay should not
 * have to leave an email and wait to be allowed to.
 */
function startRvDesk(): string {
  return `<p class="sub small" style="margin-top:16px">Ready now? $9 a rental a month, from 5 rentals.</p>
          <a class="btn wide" href="/start">Start RV Shop Desk</a>`;
}

export function renderSignIn(onDone: () => void): void {
  let mode: Mode = "in";
  const rv = isRvDesk();
  /* What they typed survives a redraw, so a failed sign-in can hand the
     address straight to the opt-in box. */
  let typed = "";
  let optinSource: "gate" | "failed_sign_in" = "gate";
  /* Supercanic's own door only, for now. Anthony, 2026-10-21: "test it
     on Supercanic version first." A shop's branded door and RV Desk keep
     the two fields until it has been tried here. */
  let google = false;
  const fillGoogle = (): void => {
    const slot = document.getElementById("gate-google");
    if (!slot || !google) return;
    slot.innerHTML = `<div class="gate-or"><span>or</span></div>
      <button type="button" id="gate-google-btn" class="btn wide">Continue with Google</button>`;
    document.getElementById("gate-google-btn")?.addEventListener("click", async () => {
      try { await signInWithGoogle(); }
      catch (err) { draw(err instanceof Error ? err.message : String(err)); }
    });
  };
  if (!configNow().shop && !rv) {
    void googleSignInAvailable().then((on) => { google = on; fillGoogle(); });
  }

  const draw = (msg = "", busy = false): void => {
    mount(`
      <div class="gate${rv ? " gate-rv" : ""}">
        <div class="card gate-card">
          ${brandMark()}
          <h1 class="gate-word">${wordmark(brandName())}</h1>
          <p class="sub">${configNow().stage === "staging"
            ? "Staging. Test data only — nothing here reaches a real customer."
            : rv ? (mode === "optin"
              ? "Not on RV Shop Desk yet? Leave your email and we will send you how it works and what it costs."
              : "Sign in to your desk.")
            : "Sign in to your shop."}</p>

          <div class="tabs">
            <button class="tab ${mode === "in" ? "on" : ""}" data-mode="in">Sign in</button>
            ${rv
              ? `<button class="tab ${mode === "optin" ? "on" : ""}" data-mode="optin">Get RV Shop Desk</button>`
              : `<button class="tab ${mode === "up" ? "on" : ""}" data-mode="up">Create a login</button>`}
          </div>

          ${mode === "optin" ? `
          <form id="gate-optin" novalidate>
            <label class="field">
              <span>Email</span>
              <input id="gate-email" type="email" autocomplete="email" required value="${esc(typed)}">
            </label>
            <button class="btn primary wide" type="submit" ${busy ? "disabled" : ""}>
              ${busy ? "Working…" : "Keep me posted"}</button>
            ${msg ? `<p class="msg ${/^You're on/.test(msg) ? "good" : "bad"}">${esc(msg)}</p>` : ""}
          </form>
          ${startRvDesk()}` : `
          <form id="gate-form" novalidate>
            <label class="field">
              <span>Email</span>
              <input id="gate-email" type="email" autocomplete="email" required value="${esc(typed)}">
            </label>
            <label class="field">
              <span>Password</span>
              <input id="gate-pass" type="password"
                     autocomplete="${mode === "in" ? "current-password" : "new-password"}" required>
            </label>
            <button class="btn primary wide" type="submit" ${busy ? "disabled" : ""}>
              ${busy ? "Working…" : mode === "in" ? "Sign in" : "Create the login"}
            </button>
            ${msg ? `<p class="msg ${/^Check|^Created/.test(msg) ? "good" : "bad"}">${esc(msg)}</p>` : ""}
            ${mode === "in"
              ? `<button type="button" id="gate-forgot" class="linkbtn" style="margin-top:12px">Forgot your password?</button>`
              : ""}
          </form>
          <div id="gate-google"></div>`}
          ${brandFoot()}
        </div>
      </div>`);

    fillGoogle();

    document.querySelectorAll<HTMLButtonElement>(".tab").forEach((b) =>
      b.addEventListener("click", () => {
        typed = (document.getElementById("gate-email") as HTMLInputElement | null)?.value.trim() ?? typed;
        mode = b.dataset["mode"] as Mode;
        optinSource = "gate";
        draw();
      }));

    document.getElementById("gate-optin")?.addEventListener("submit", async (e) => {
      e.preventDefault();
      typed = (document.getElementById("gate-email") as HTMLInputElement).value.trim();
      if (!typed) { draw("Your email, please."); return; }
      draw("", true);
      try {
        const r = await fetch(api("/api/rvdesk/optin"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: typed, source: optinSource }),
        });
        const d = await r.json().catch(() => ({})) as { ok?: boolean; error?: string };
        if (!r.ok || !d.ok) { draw(d.error ?? "That did not save. Try again in a minute."); return; }
        draw("You're on the list. Watch your email for how RV Shop Desk works and what it costs.");
      } catch (err) {
        draw(err instanceof Error ? err.message : String(err));
      }
    });

    /**
      * The email that welcomes a new shop tells them to use this, so it has
      * to exist. It sends to whatever is in the email box rather than
      * asking again -- one field is already filled in by the time anybody
      * reaches for this link.
      */
    document.getElementById("gate-forgot")?.addEventListener("click", async () => {
      const email = (document.getElementById("gate-email") as HTMLInputElement).value.trim();
      if (!email) { draw("Put your email in first, then tap it again."); return; }
      draw("", true);
      try {
        await sendReset(email);
        // "Check your email" and nothing else: saying whether the address
        // was found would tell a stranger which of your staff have logins.
        draw("Check your email for a link to pick a new password.");
      } catch (err) {
        draw(err instanceof Error ? err.message : String(err));
      }
    });

    document.getElementById("gate-form")?.addEventListener("submit", async (e) => {
      e.preventDefault();
      const email = (document.getElementById("gate-email") as HTMLInputElement).value.trim();
      const pass = (document.getElementById("gate-pass") as HTMLInputElement).value;
      if (!email || !pass) { draw("Both fields, please."); return; }
      draw("", true);
      try {
        if (mode === "in") {
          try {
            await signIn(email, pass);
          } catch (err) {
            if (!rv) throw err;
            /* A failed sign-in on RV Desk becomes the opt-in, address
               kept. Worded so a real customer who mistyped a password
               still sees the way back. */
            typed = email; mode = "optin"; optinSource = "failed_sign_in";
            draw("That login did not work. If you already have one, go back to Sign in and "
              + "use Forgot your password. If not, leave your email here.");
            return;
          }
          onDone();
        } else {
          const { needsConfirmation } = await signUp(email, pass);
          if (needsConfirmation) {
            // Said plainly, because a silent "nothing happened" here is
            // the most common reason somebody gives up on a new account.
            draw("Check your email to confirm the address, then sign in.");
          } else {
            onDone();
          }
        }
      } catch (err) {
        draw(err instanceof Error ? err.message : String(err));
      }
    });
  };

  draw();
}

/**
 * Signed in, belonging to nothing. On staging there is a button; on
 * production there is an explanation, because being let into a shop is
 * somebody's decision and not a thing you grant yourself.
 */
export function renderNoProfile(who: Extract<Who, { state: "no-profile" }>, onDone: () => void): void {
  const staging = configNow().stage === "staging";
  const draw = (msg = "", busy = false): void => {
    mount(`
      <div class="gate">
        <div class="card gate-card">
          <h1>Almost in</h1>
          <p class="sub">You are signed in as <b>${esc(who.session.user.email ?? "")}</b>, but this
             login does not belong to a shop yet — so every screen would be empty.</p>
          ${staging ? `
            <button class="btn primary wide" id="join" ${busy ? "disabled" : ""}>
              ${busy ? "Joining…" : "Join the test shop as admin"}</button>
            <p class="sub small">Staging only. It attaches you to the seeded test shop —
               six customers, four jobs — and nothing else exists to attach you to.</p>
          ` : `
            <p class="sub">Ask an administrator at your shop to add you. They will need
               the address above.</p>
          `}
          ${msg ? `<p class="msg bad">${esc(msg)}</p>` : ""}
          <button class="btn ghost wide" id="out">Sign out</button>
        </div>
      </div>`);

    document.getElementById("out")?.addEventListener("click", async () => { await signOut(); onDone(); });
    document.getElementById("join")?.addEventListener("click", async () => {
      draw("", true);
      try { await joinTestShop("admin"); onDone(); }
      catch (e) { draw(e instanceof Error ? e.message : String(e)); }
    });
  };
  draw();
}

